import {Avatar} from 'platform/components';
import {Center, ThemeColorPath} from 'platform/foundation';
import styled from 'styled-components';

import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, useFocusControl, useUserFullName} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useIsMobileNavigation} from '../hooks/useIsMobileNavigation';
import {NavigationButton} from './NavigationButton';

interface MyProfileAndSettingsButtonProps extends TestIdProps {
  isOpen: boolean;
  isAway: boolean;
  userAvatarUrl?: string;
}

interface AvatarWithAwayIndicatorProps extends TestIdProps {
  isAway: boolean;
  color: ThemeColorPath;
  name?: string | null;
  userAvatarUrl?: string;
}

function AvatarWithAwayIndicator(props: AvatarWithAwayIndicatorProps) {
  return (
    <AvatarWrapper>
      <Avatar
        name={props.name}
        size="xSmall"
        variant="circle"
        color={props.color}
        imageUrl={props.userAvatarUrl}
      />
      {props.isAway ? (
        <AwayIndicator
          title={i18n.t('user.awayStatus.awayBadge')}
          data-testid={suffixTestId('awayIndicator', props)}
        />
      ) : null}
    </AvatarWrapper>
  );
}

function UserAvatarButtonMobile(props: MyProfileAndSettingsButtonProps) {
  const userFullName = useUserFullName();
  const {isDisabled} = useFocusControl();

  return (
    <NavigationButton
      isActive={props.isOpen}
      tabIndex={isDisabled ? -1 : 0}
      data-testid={props['data-testid']}
    >
      <AvatarWithAwayIndicator
        name={userFullName}
        color={props.isOpen ? 'palettes.neutral.40.100' : 'palettes.neutral.30.100'}
        userAvatarUrl={props.userAvatarUrl}
        isAway={props.isAway}
        data-testid={props['data-testid']}
      />
    </NavigationButton>
  );
}

interface AvatarIconControlProps extends TestIdProps {
  isOpen: boolean;
  isAway: boolean;
  userAvatarUrl?: string;
}

const UserAvatarButtonDesktop = (props: AvatarIconControlProps) => {
  const userFullName = useUserFullName();
  const {isDisabled} = useFocusControl();

  return (
    <Center width="100%" height="100%">
      <HeaderButtonWrapper
        $isOpen={props.isOpen}
        tabIndex={isDisabled ? -1 : 0}
        data-testid={props['data-testid']}
      >
        <AvatarWithAwayIndicator
          name={userFullName ?? EMPTY_PLACEHOLDER}
          color={props.isOpen ? 'palettes.neutral.40.100' : 'palettes.neutral.30.100'}
          userAvatarUrl={props.userAvatarUrl}
          isAway={props.isAway}
          data-testid={props['data-testid']}
        />
      </HeaderButtonWrapper>
    </Center>
  );
};

export function UserAvatarButton(props: MyProfileAndSettingsButtonProps) {
  const isMobileNavigation = useIsMobileNavigation();

  if (isMobileNavigation) {
    return <UserAvatarButtonMobile {...props} />;
  }

  return <UserAvatarButtonDesktop {...props} />;
}

interface HeaderButtonWrapperProps {
  $isOpen: boolean;
}

const HeaderButtonWrapper = styled.div<HeaderButtonWrapperProps>`
  background-color: ${({theme, $isOpen}) => $isOpen && theme.colors.palettes.neutral[30][100]};
  height: ${({theme}) => theme.getSize(6)};
  width: ${({theme}) => theme.getSize(6)};
  border-radius: 50%;
  cursor: pointer;
  transition: box-shadow 250ms ease-in-out;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({theme}) => theme.getSize(1)}
      ${({theme}) => theme.colors.palettes.blue[40][100]};
  }

  &:hover {
    background-color: ${({theme}) => theme.colors.palettes.neutral[30][100]};
  }
`;

const AvatarWrapper = styled.div`
  position: relative;
  display: flex;
`;

const AwayIndicator = styled.div`
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: ${({theme}) => theme.getSize(2)};
  height: ${({theme}) => theme.getSize(2)};
  border-radius: 50%;
  background-color: ${({theme}) => theme.colors.palettes.yellow[60][100]};
  border: 1px solid ${({theme}) => theme.colors.general.white};
`;
